/* ═══════════════════════════════════════════════════
   BISOU GRADIENT — Shared WebGL Custom Element
   Used by: bisou-gradient.liquid, catch-vote-banner.liquid
   ═══════════════════════════════════════════════════ */
if (!window.customElements.get('bisou-gradient')) {
  class BisouGradient extends HTMLElement {
    constructor() {
      super();
      this.canvas = this.querySelector('canvas');
      this.settingsNode = this.querySelector('.gradient-settings');
      this.gl = this.canvas ? this.canvas.getContext('webgl') : null;
      this.rafId = null;
      this.locs = {};
      this.params = {};
      this.frameCount = 0;
      if (this.settingsNode) {
        try { this.params = JSON.parse(this.settingsNode.textContent); } catch (e) {}
      }
    }

    connectedCallback() {
      console.log('[bisou-gradient] connected', this);
      console.log('[bisou-gradient] gl context acquired:', !!this.gl);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        console.log('[bisou-gradient] reduced motion → hidden');
        this.style.display = 'none';
        return;
      }
      if (!this.gl) {
        console.warn('[bisou-gradient] no GL context → abort');
        return;
      }
      this._initialized = false;

      var self = this;
      var startObserver = function () {
        self._observer = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            console.log('[bisou-gradient] intersect:', entry.isIntersecting, 'rect:', entry.boundingClientRect.width + 'x' + entry.boundingClientRect.height);
            if (entry.isIntersecting) {
              if (!self._initialized) {
                self.initWebGL();
                self.setupResize();
                self._initialized = true;
                self.render();
              } else if (!self.rafId) {
                self.render();
              }
            } else {
              if (self.rafId) {
                cancelAnimationFrame(self.rafId);
                self.rafId = null;
              }
            }
          });
        }, { threshold: 0, rootMargin: '500px' });
        self._observer.observe(self);
      };

      /* Guard : si dimensions nulles au mount, attendre 1 frame */
      if (this.offsetWidth === 0 || this.offsetHeight === 0) {
        console.log('[bisou-gradient] zero dimensions at mount (' + this.offsetWidth + 'x' + this.offsetHeight + ') → deferring 1 frame');
        requestAnimationFrame(startObserver);
      } else {
        startObserver();
      }
    }

    disconnectedCallback() {
      if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null; }
      if (this._observer) { this._observer.disconnect(); this._observer = null; }
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
    }

    hexToRgb(hex) {
      var r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return r ? [parseInt(r[1],16)/255, parseInt(r[2],16)/255, parseInt(r[3],16)/255] : [0,0,0];
    }

    compileShader(gl, src, type) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return s;
    }

    initWebGL() {
      var gl = this.gl;
      var vs = 'attribute vec2 position;void main(){gl_Position=vec4(position,0.0,1.0);}';
      var fs = [
        'precision highp float;',
        'uniform vec2 iResolution;uniform float iTime;',
        'uniform vec3 uC1,uC2,uC3,uC4,uC5,uC6;',
        'uniform vec2 uP1,uP2,uP3,uP4,uP5,uP6;',
        'uniform float uS1,uS2,uS3,uS4,uS5,uS6;',
        'uniform float uA1,uA2,uA3,uA4,uA5,uA6;',
        'uniform float uOffsetX,uOffsetY,uScale,uSpeed,uMoveAmp,uBrightness,uBlend,uWarpStrength,uWarpIters,uGrainInt,uGrainSize;',
        'float hash(vec2 p){return fract(sin(dot(p,vec2(12.9898,78.233)))*43758.5453);}',
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/iResolution.xy;uv.x*=iResolution.x/iResolution.y;',
        '  uv.x+=uOffsetX;uv.y+=uOffsetY;float t=iTime*uSpeed;vec2 p=uv*uScale;',
        '  for(float i=1.0;i<15.0;i++){if(i>uWarpIters)break;vec2 np=p;',
        '    np.x+=uWarpStrength/i*sin(i*p.y+t*0.5+0.3*i);',
        '    np.y+=uWarpStrength/i*cos(i*p.x+t*0.5+0.3*i);p=np;}',
        '  vec2 pts[6];',
        '  pts[0]=uP1+vec2(sin(t*0.4),cos(t*0.5))*uA1*uMoveAmp;',
        '  pts[1]=uP2+vec2(cos(t*0.6),sin(t*0.3))*uA2*uMoveAmp;',
        '  pts[2]=uP3+vec2(sin(t*0.5),cos(t*0.7))*uA3*uMoveAmp;',
        '  pts[3]=uP4+vec2(cos(t*0.2),sin(t*0.8))*uA4*uMoveAmp;',
        '  pts[4]=uP5+vec2(sin(t*0.9),cos(t*0.1))*uA5*uMoveAmp;',
        '  pts[5]=uP6+vec2(cos(t*0.1),sin(t*0.9))*uA6*uMoveAmp;',
        '  vec3 cols[6];cols[0]=uC1;cols[1]=uC2;cols[2]=uC3;cols[3]=uC4;cols[4]=uC5;cols[5]=uC6;',
        '  float sizes[6];sizes[0]=uS1;sizes[1]=uS2;sizes[2]=uS3;sizes[3]=uS4;sizes[4]=uS5;sizes[5]=uS6;',
        '  vec3 sum=vec3(0.0);float val=0.0;',
        '  for(int i=0;i<6;i++){float d=length(p-pts[i]);if(d==0.0)d=0.0001;',
        '    float w=sizes[i]/pow(d,uBlend);sum+=w*cols[i];val+=w;}',
        '  sum/=val;sum*=uBrightness;',
        '  float grain=(hash((gl_FragCoord.xy*uGrainSize)+t)-0.5)*uGrainInt;sum+=grain;',
        '  gl_FragColor=vec4(sum,1.0);}'
      ].join('\n');

      var prog = gl.createProgram();
      gl.attachShader(prog, this.compileShader(gl, vs, gl.VERTEX_SHADER));
      gl.attachShader(prog, this.compileShader(gl, fs, gl.FRAGMENT_SHADER));
      gl.linkProgram(prog);
      gl.useProgram(prog);

      var verts = new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]);
      var buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
      var pos = gl.getAttribLocation(prog, 'position');
      gl.enableVertexAttribArray(pos);
      gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

      var u = function(n) { return gl.getUniformLocation(prog, n); };
      this.locs = {
        iResolution:u('iResolution'), iTime:u('iTime'),
        uC1:u('uC1'),uC2:u('uC2'),uC3:u('uC3'),uC4:u('uC4'),uC5:u('uC5'),uC6:u('uC6'),
        uP1:u('uP1'),uP2:u('uP2'),uP3:u('uP3'),uP4:u('uP4'),uP5:u('uP5'),uP6:u('uP6'),
        uS1:u('uS1'),uS2:u('uS2'),uS3:u('uS3'),uS4:u('uS4'),uS5:u('uS5'),uS6:u('uS6'),
        uA1:u('uA1'),uA2:u('uA2'),uA3:u('uA3'),uA4:u('uA4'),uA5:u('uA5'),uA6:u('uA6'),
        uOffsetX:u('uOffsetX'),uOffsetY:u('uOffsetY'),uScale:u('uScale'),uSpeed:u('uSpeed'),
        uMoveAmp:u('uMoveAmp'),uBrightness:u('uBrightness'),uBlend:u('uBlend'),
        uWarpStrength:u('uWarpStrength'),uWarpIters:u('uWarpIters'),
        uGrainInt:u('uGrainInt'),uGrainSize:u('uGrainSize')
      };
    }

    setupResize() {
      this.resizeHandler = function() {
        var rect = this.getBoundingClientRect();
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        this.canvas.width = rect.width * dpr;
        this.canvas.height = rect.height * dpr;
        this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        this.gl.uniform2f(this.locs.iResolution, this.canvas.width, this.canvas.height);
      }.bind(this);
      window.addEventListener('resize', this.resizeHandler);
      this.resizeHandler();
    }

    render(time) {
      time = time || 0;
      if (this.frameCount < 5) {
        console.log('[bisou-gradient] render frame', this.frameCount, 'canvas=' + this.canvas.width + 'x' + this.canvas.height);
        this.frameCount++;
      }
      var gl = this.gl, p = this.params, l = this.locs;
      gl.uniform1f(l.iTime, (time * 0.0025) + (p.timeOffset || 0));
      gl.uniform3fv(l.uC1,this.hexToRgb(p.color1));gl.uniform3fv(l.uC2,this.hexToRgb(p.color2));
      gl.uniform3fv(l.uC3,this.hexToRgb(p.color3));gl.uniform3fv(l.uC4,this.hexToRgb(p.color4));
      gl.uniform3fv(l.uC5,this.hexToRgb(p.color5));gl.uniform3fv(l.uC6,this.hexToRgb(p.color6));
      gl.uniform2f(l.uP1,p.p1x,p.p1y);gl.uniform2f(l.uP2,p.p2x,p.p2y);
      gl.uniform2f(l.uP3,p.p3x,p.p3y);gl.uniform2f(l.uP4,p.p4x,p.p4y);
      gl.uniform2f(l.uP5,p.p5x,p.p5y);gl.uniform2f(l.uP6,p.p6x,p.p6y);
      gl.uniform1f(l.uS1,p.size1);gl.uniform1f(l.uS2,p.size2);gl.uniform1f(l.uS3,p.size3);
      gl.uniform1f(l.uS4,p.size4);gl.uniform1f(l.uS5,p.size5);gl.uniform1f(l.uS6,p.size6);
      gl.uniform1f(l.uA1,p.amp1);gl.uniform1f(l.uA2,p.amp2);gl.uniform1f(l.uA3,p.amp3);
      gl.uniform1f(l.uA4,p.amp4);gl.uniform1f(l.uA5,p.amp5);gl.uniform1f(l.uA6,p.amp6);
      gl.uniform1f(l.uOffsetX,p.offsetX);gl.uniform1f(l.uOffsetY,p.offsetY);
      gl.uniform1f(l.uScale,p.scale);gl.uniform1f(l.uSpeed,p.speed);
      gl.uniform1f(l.uMoveAmp,p.moveAmp);gl.uniform1f(l.uBrightness,p.brightness);
      gl.uniform1f(l.uBlend,p.blend);gl.uniform1f(l.uWarpStrength,p.warpStrength);
      gl.uniform1f(l.uWarpIters,p.warpIters);gl.uniform1f(l.uGrainInt,p.grainInt);
      gl.uniform1f(l.uGrainSize,p.grainSize);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      this.rafId = requestAnimationFrame(this.render.bind(this));
    }
  }
  customElements.define('bisou-gradient', BisouGradient);
}
